import { replaceBlockText, runsFor, sliceRuns, withRuns,splitRichParagraph,deletionMarksFor } from '../../lib/scriptRichText';
import type { ScriptBlockData } from './ScriptBlock';
import type { BlockType } from './SlashMenu';
import {dualFor, dualText, withDual} from '../../lib/scriptDualDialogue';
import {cloneAlternates} from '../../lib/scriptAlternates';

const returnTypes: Record<BlockType, BlockType> = {
    'scene-heading': 'action', action: 'action', character: 'dialogue',
    dialogue: 'action', parenthetical: 'dialogue', transition: 'scene-heading',
    shot: 'action', 'dual-dialogue': 'action',
};

/** FD11 Windows manual pp. 329, 339; this is a documented workflow,
 * not a recovered native keyboard dispatcher. */
export function tabTransition(block: ScriptBlockData): {type: BlockType; insert: boolean} | undefined {
    const empty = !block.content.trim() || block.type === 'parenthetical' && block.content.trim() === '()';
    const types: Partial<Record<BlockType, BlockType>> = {
        'scene-heading': 'action', action: 'character',
        character: empty ? 'transition' : 'parenthetical',
        transition: 'scene-heading', dialogue: 'parenthetical', parenthetical: 'dialogue',
    };
    const type = types[block.type];
    return type ? {type, insert: !empty} : undefined;
}

export type ScriptCaretRequest = {start: number; end: number};

export type BlockInsertion =
    | { kind: 'split'; before: string; after: string }
    | { kind: 'insert'; type?: BlockType }
    | { kind: 'duplicate' };

export function speakerName(cue: string): string {
    return cue.trim().replace(/(?:\s*\([^)]*\))+\s*$/, '').trim().toUpperCase();
}

function readableDual(content: string): string {
    try {
        const data = JSON.parse(content);
        const left = data.left || data.LEFT || {};
        const right = data.right || data.RIGHT || {};
        return [left.character || left.CHARACTER, left.dialogue || left.DIALOGUE,
            right.character || right.CHARACTER, right.dialogue || right.DIALOGUE]
            .filter(value => typeof value === 'string' && value.length > 0).join('\n');
    } catch { return content; }
}

export function changeElement(block: ScriptBlockData, type: BlockType): ScriptBlockData {
    if (block.type === type) return block;
    if((type==='dual-dialogue'||block.type==='dual-dialogue')&&[block,...(block.dualDialogue?[...block.dualDialogue.left,...block.dualDialogue.right]:[])].some(p=>deletionMarksFor(p).length))return block;
    if(block.alternates && type==='dual-dialogue')return block;
    if(block.dualDialogue && type!=='dual-dialogue' && [...block.dualDialogue.left,...block.dualDialogue.right].some(p=>p.alternates))return block;
    if (block.dualDialogue && type!=='dual-dialogue') {
        const data=dualFor(block), runs:ReturnType<typeof runsFor>=[];
        (['left','right'] as const).forEach((side,sideIndex) => data[side].forEach((part,index) => {
            if (index || sideIndex) runs.push({text:index ? '\n' : '\n\n',attributes:{}});
            runs.push(...runsFor(part));
        }));
        const attrs={...data.paragraphAttributes};
        delete attrs.Type;
        const flattened=withRuns({...block,type:'action',dualDialogue:undefined,characterId:undefined},runs,attrs);
        return type==='action' ? flattened : changeElement(flattened,type);
    }
    let content = block.type === 'dual-dialogue' ? (block.dualDialogue ? dualText(dualFor(block)) : readableDual(block.content)) : block.content;
    if (type === 'dual-dialogue') {
        content = JSON.stringify({
            left: { character: block.type === 'character' ? content : '',
                dialogue: block.type === 'character' ? '' : content },
            right: { character: '', dialogue: '' },
        });
    } else {
        if (['scene-heading', 'character', 'transition', 'shot'].includes(type)) {
            content = content.toUpperCase();
        }
        if (type === 'parenthetical') {
            if (!content.startsWith('(')) content = `(${content}`;
            if (!content.endsWith(')')) content = `${content})`;
        }
    }
    return { ...replaceBlockText(block, content), type, dualDialogue:undefined, richText: type === 'dual-dialogue' || block.type === 'dual-dialogue' ? undefined : replaceBlockText(block, content).richText,
        characterId: type === 'character' && block.type === 'character' ? block.characterId : undefined };
}

export function insertScriptBlock(
    blocks: ScriptBlockData[], afterId: string, request: BlockInsertion,
    id = crypto.randomUUID(),
    revisionId?:string,trackDeletions=false,
): ScriptBlockData[] {
    const index = blocks.findIndex(block => block.id === afterId);
    if (index < 0) return blocks;
    const current = blocks[index];
    let next: ScriptBlockData = request.kind === 'duplicate' ? { ...current, id } : {
        id, type: request.kind === 'insert' ? request.type || returnTypes[current.type]
            : returnTypes[current.type],
        content: request.kind === 'split' ? request.after : request.kind === 'insert' && request.type === 'parenthetical' ? '()' : '',
    };
    const result = [...blocks];
    if (request.kind === 'split') {
        if(current.richText||revisionId){[result[index],next]=splitRichParagraph(current,request.before.length,current.content.length-request.after.length,next,revisionId,trackDeletions);}
        else result[index]=replaceBlockText(current,request.before);
    } else if (request.kind === 'duplicate' && current.richText) {
        const attributes = {...current.richText.paragraphAttributes};
        for (const key of ['Number', 'Bookmark', 'Label']) delete attributes[key];
        next = withRuns(next, runsFor(current), attributes);
    }
    if (request.kind==='duplicate' && current.dualDialogue) {
        const data=dualFor(current);
        const clone=(parts:typeof data.left) => parts.map(p => {
            const attrs={...p.richText?.paragraphAttributes};
            for (const key of ['Number','Bookmark','Label']) delete attrs[key];
            return {...p,id:crypto.randomUUID(),alternates:cloneAlternates(p.alternates),richText:p.richText ? {...p.richText,paragraphAttributes:attrs,runs:p.richText.runs.map(r => ({...r,attributes:{...r.attributes}}))} : undefined};
        });
        const attrs={...data.paragraphAttributes};
        for (const key of ['Number','Bookmark','Label']) delete attrs[key];
        next=withDual(next,{...data,paragraphAttributes:attrs,left:clone(data.left),right:clone(data.right)});
    }
    result.splice(index + 1, 0, next);
    if(request.kind==='duplicate')result[index+1]={...next,alternates:cloneAlternates(current.alternates)};
    return result;
}
